import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { BRAND } from "@/shared/brand";

/**
 * Application branding — **the single place to change the logo.**
 *
 * Drop your artwork at `web/client/public/brand/logo.svg` (SVG preferred; `logo.png` at 2x also
 * works — change LOGO_SRC below to match). Nothing else needs editing: until the file exists, both
 * components fall back to the built-in mark, so the app never renders a broken image.
 *
 * A wordmark that already contains the product name replaces the whole lockup — use <BrandLogo>.
 * A square/standalone symbol goes in the tinted tile — use <BrandMark>.
 *
 * The PWA icons in `web/client/public/icons/` are separate files and must be replaced too: those
 * are what appear in the browser tab and on a phone home screen, and they have to be square.
 */

/** Path under `public/`. Change the extension here if you save a PNG instead. */
const LOGO_SRC = "/brand/logo.png";

/**
 * Set true when the artwork is a single-colour silhouette — e.g. a white-on-transparent wordmark.
 * The file is then used as a *mask* and painted in the surrounding text colour, so one asset stays
 * legible in both themes: near-black slate on the light surfaces, near-white on the dark ones.
 *
 * The current logo.png is exactly that (222x52, every opaque pixel pure white, 71% transparent).
 * Rendered as a plain image it vanished against the light-mode app bar and login card, both of
 * which are `background.paper`.
 *
 * Leave this false for full-colour artwork — masking would flatten it to a single colour.
 */
const LOGO_IS_MONOCHROME = true;

/**
 * Set true if your artwork is white/light and needs a dark plate behind it to be visible.
 * Only consulted for full-colour artwork: a monochrome mark is recoloured to suit the theme by
 * LOGO_IS_MONOCHROME above and needs no plate.
 */
const LOGO_NEEDS_DARK_PLATE = false;

/** Matches the viewBox of the fallback artwork below. */
const FALLBACK_VIEWBOX = "0 0 24 24";
const FALLBACK_PATHS = (
  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
);

type Artwork =
  | { state: "loading" }
  | { state: "ready"; aspect: number }
  | { state: "missing" };

/**
 * Loads the logo once to learn whether it exists and what shape it is. A masked element has no
 * intrinsic size, so the mask path needs the aspect ratio measured rather than assumed — which
 * also keeps this file honest about its promise that dropping in *any* artwork just works.
 */
function useLogoArtwork(): Artwork {
  const [artwork, setArtwork] = useState<Artwork>({ state: "loading" });

  useEffect(() => {
    let alive = true;
    const probe = new Image();
    probe.onload = () => {
      if (alive) {
        setArtwork({ state: "ready", aspect: probe.naturalWidth / Math.max(probe.naturalHeight, 1) });
      }
    };
    probe.onerror = () => { if (alive) setArtwork({ state: "missing" }); };
    probe.src = LOGO_SRC;
    return () => { alive = false; };
  }, []);

  return artwork;
}

/**
 * The full brand lockup: your wordmark if one is present, otherwise the symbol plus the product
 * name as text. Used in the sidebar and on the login screen.
 */
export function BrandLogo({ height = 30 }: { height?: number }) {
  const artwork = useLogoArtwork();

  // No logo file — the symbol and the name, so the header is never empty.
  if (artwork.state === "missing") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <BrandMark size={height} />
        <Typography variant="h6" noWrap sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
          {BRAND.name}
        </Typography>
      </Box>
    );
  }

  if (LOGO_IS_MONOCHROME) {
    // Painted in currentColor, which both hosts (the app bar and the login card) set to
    // text.primary — so the mark reads as part of the type, in either theme.
    // A rough aspect stands in until the probe resolves, so the bar does not visibly reflow.
    const aspect = artwork.state === "ready" ? artwork.aspect : 4;
    return (
      <Box
        role="img"
        aria-label={BRAND.name}
        sx={{
          height,
          width: height * aspect,
          maxWidth: 200,
          flexShrink: 0,
          bgcolor: "currentColor",
          WebkitMaskImage: `url("${LOGO_SRC}")`,
          maskImage: `url("${LOGO_SRC}")`,
          WebkitMaskSize: "contain",
          maskSize: "contain",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "left center",
          maskPosition: "left center",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", minWidth: 0,
        ...(LOGO_NEEDS_DARK_PLATE
          ? { bgcolor: "primary.main", borderRadius: 1.5, px: 1, py: 0.5 }
          : {}),
      }}
    >
      <Box
        component="img"
        src={LOGO_SRC}
        alt={BRAND.name}
        sx={{ height, width: "auto", maxWidth: 200, display: "block" }}
      />
    </Box>
  );
}

/** The square symbol alone, in its tinted tile. For tight spots where the wordmark won't fit. */
export function BrandMark({ size = 30 }: { size?: number }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size, height: size, borderRadius: 1.5, flexShrink: 0,
        bgcolor: "primary.main", color: "primary.contrastText",
        display: "grid", placeItems: "center",
      }}
    >
      <svg width={size * 0.57} height={size * 0.57} viewBox={FALLBACK_VIEWBOX} fill="none">
        {FALLBACK_PATHS}
      </svg>
    </Box>
  );
}
